import { needsBacklogGrant, type BacklogConnectionState } from '@/hooks/useBacklogConnection'
import { fmt, type UiStrings } from '@/constants/ui-locale'

/**
 * The reader's own Backlog grant, shown beside the sync it runs under.
 *
 * One way to connect, not a choice: connecting is approving this application on the space. The API
 * key box this replaced is gone rather than kept alongside — two ways to connect on one card is a
 * question the reader should not have to answer.
 *
 * It is begun wherever it is shown. This used to hand off to the settings
 * screen from everywhere else, because the application registers exactly one
 * redirect URI (`BACKLOG_REDIRECT_URI`) and the approval therefore always
 * lands there — which stranded the reader on a page they never asked for.
 * `backlogReturnPath` now writes down the screen the trip began on and the
 * callback sends them back to it, so there is nowhere left to hand off to.
 *
 * Presentational: the state lives in {@link useBacklogConnection} because the
 * Sync button depends on the same answer, and two copies of it would disagree
 * the moment one of them refreshed.
 *
 * Renders nothing at all once a grant works: the acknowledgement lives in
 * {@link BacklogConnectedBadge} up beside the Sync button, where it answers
 * the question actually being asked — whether pressing that button will work.
 * There is nothing to do about a grant that works, so a card at the foot of the
 * minutes would be chrome sitting on top of them.
 */
export default function BacklogConnectionPanel({
  connection: state,
  t,
}: {
  connection: BacklogConnectionState
  t: UiStrings
}) {
  const { connection, phase, error } = state

  const status = connection?.status

  if (status === 'ACTIVE') return null

  const busy = phase === 'saving'
  const needsKey = needsBacklogGrant(status)

  /** Navigating here rather than in the hook keeps the departure in the thing the reader pressed. */
  async function beginSso() {
    const url = await state.startSso()
    if (url) window.location.assign(url)
  }

  return (
    <div className="mt-3 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-3">
      <p className="text-xs font-semibold">{t.backlogConnectionTitle}</p>

      <p className="mt-1 text-xs text-[var(--text-secondary)]">
        {status === 'REAUTH_REQUIRED'
          ? connection?.space
            ? fmt(t.backlogConnectionExpired, { space: connection.space })
            : t.backlogConnectionSpaceUnknown
          : status === 'CONNECTION_REQUIRED'
            ? t.backlogConnectionNone
            : ''}
      </p>

      {needsKey && (
        <div className="mt-2">
          <button
            type="button"
            onClick={() => void beginSso()}
            disabled={busy}
            className="btn-primary rounded-md px-3 py-1.5 text-xs font-semibold disabled:opacity-50"
          >
            {busy ? t.backlogConnecting : t.backlogConnectWithBacklog}
          </button>
          {/* True from any screen now: the callback puts the reader back on
              the one they left. */}
          <p className="mt-1 text-xs text-[var(--text-tertiary)]">{t.backlogSsoHint}</p>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}

      {needsKey && (
        <p className="mt-2 text-xs text-[var(--text-tertiary)]">
          {t.backlogConnectionRequiredNotice}
        </p>
      )}
    </div>
  )
}

/**
 * Green tick and the account name, for the header row beside Sync.
 *
 * Sits there rather than with the panel because it qualifies the button: it
 * says the press will go through, and whose name the ticket will carry. The
 * full label wraps when space is tight so the connected account is never
 * hidden behind an ellipsis.
 */
export function BacklogConnectedBadge({
  connection: state,
  t,
}: {
  connection: BacklogConnectionState
  t: UiStrings
}) {
  if (!state.connected) return null
  const account = state.connection?.account
  const label = account
    ? fmt(t.backlogConnectionConnected, { name: account.name })
    : t.backlogConnectionTitle

  return (
    <span
      className="flex min-w-0 items-center gap-1 text-xs font-medium text-green-600"
      title={label}
    >
      <CheckIcon className="h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 break-words">{label}</span>
    </span>
  )
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  )
}
